/**
 * Lightweight in-memory fetch cache with in-flight deduplication and TTL.
 * Used across sidebar pages to prevent request storms on mount/navigation.
 */

const cache = new Map();
const inflight = new Map();

export const DEFAULT_FETCH_TTL_MS = 15000;

/**
 * @param {string} key Stable cache key (URL + serialized params).
 * @param {() => Promise<any>} fetcher
 * @param {{ ttlMs?: number, signal?: AbortSignal, force?: boolean }} [options]
 */
export async function cachedFetch(key, fetcher, options = {}) {
  const { ttlMs = DEFAULT_FETCH_TTL_MS, signal, force = false } = options;
  const now = Date.now();

  if (!force) {
    const hit = cache.get(key);
    if (hit && now - hit.ts < ttlMs) {
      return hit.data;
    }
  }

  if (inflight.has(key)) {
    return inflight.get(key);
  }

  const promise = Promise.resolve()
    .then(() => fetcher(signal))
    .then((data) => {
      cache.set(key, { data, ts: Date.now() });
      inflight.delete(key);
      return data;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });

  inflight.set(key, promise);
  return promise;
}

/** Invalidate one key or all keys with a prefix. */
export function invalidateFetchCache(keyOrPrefix) {
  if (!keyOrPrefix) {
    cache.clear();
    inflight.clear();
    return;
  }
  for (const key of [...cache.keys(), ...inflight.keys()]) {
    if (key === keyOrPrefix || key.startsWith(keyOrPrefix)) {
      cache.delete(key);
      inflight.delete(key);
    }
  }
}

/** Build a stable cache key from parts. */
export function fetchCacheKey(...parts) {
  return parts.filter((p) => p != null && p !== '').join('::');
}

/**
 * Hook helper: track mounted state to avoid setState after unmount.
 */
export function createMountedRef() {
  return { current: true };
}

export function useAbortControllerRef() {
  const ref = { current: null };
  ref.reset = () => {
    if (ref.current) {
      ref.current.abort();
    }
    ref.current = new AbortController();
    return ref.current;
  };
  ref.abort = () => {
    if (ref.current) {
      ref.current.abort();
      ref.current = null;
    }
  };
  return ref;
}
